import React, { useEffect, useRef, useState } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Address } from '../types/generic';
import { Clear, Search } from '@mui/icons-material';
import {
    IconButton,
    InputAdornment,
    TextField,
    TextFieldProps,
} from '@mui/material';

interface PlaceAutocompleteProps extends Omit<TextFieldProps, 'variant'> {
    onPlaceSelect: (
        place: google.maps.places.PlaceResult | null,
        selectedLabel?: string,
    ) => void;
    height?: string;
    options?: google.maps.places.AutocompleteOptions;
    initialInputValue?: string;
    /** When true, empty the input after a Places pick (e.g. multi-location chips). */
    clearOnSelect?: boolean;
}

const PlaceAutocomplete = ({
    onPlaceSelect,
    height,
    options,
    initialInputValue,
    clearOnSelect = false,
    ...props
}: PlaceAutocompleteProps) => {
    const [placeAutocomplete, setPlaceAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const places = useMapsLibrary('places');

    useEffect(() => {
        if (!places || !inputRef.current) return;

        setPlaceAutocomplete(
            new places.Autocomplete(inputRef.current, options),
        );
    }, [places]);

    useEffect(() => {
        if (!placeAutocomplete) return;
        placeAutocomplete.setOptions(options || {});
    }, [placeAutocomplete, options]);

    useEffect(() => {
        if (!placeAutocomplete) return;

        placeAutocomplete.addListener('place_changed', () => {
            const selectedLabel = inputRef.current?.value || '';
            onPlaceSelect(placeAutocomplete.getPlace(), selectedLabel);
            if (clearOnSelect && inputRef.current) {
                inputRef.current.value = '';
            }
        });

        return () => {
            google.maps.event.clearListeners(
                placeAutocomplete,
                'place_changed',
            );
        };
    }, [clearOnSelect, onPlaceSelect, placeAutocomplete]);

    useEffect(() => {
        if (!inputRef.current || typeof initialInputValue !== 'string') return;
        inputRef.current.value = initialInputValue;
    }, [initialInputValue]);

    const handleClear = () => {
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    return (
        <div className='autocomplete-container'>
            <TextField
                size='small'
                placeholder='Search or enter manually.'
                variant='outlined'
                fullWidth
                inputRef={inputRef}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position='start'>
                            <Search fontSize='small' />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position='end'>
                            <IconButton
                                onClick={handleClear}
                                disableRipple
                                color='primary'
                            >
                                <Clear fontSize='small' />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                {...props}
            />
        </div>
    );
};

interface AddressAutocompleteProps extends Omit<
    TextFieldProps,
    'variant' | 'onChange'
> {
    onChange: (data: Address) => void;
    height?: string;
    options?: google.maps.places.AutocompleteOptions;
    placeholder?: string;
    initialInputValue?: string;
    clearOnSelect?: boolean;
}

const defaultOptions: google.maps.places.AutocompleteOptions = {
    fields: [
        'address_components',
        'geometry',
        'name',
        'formatted_address',
        'place_id',
    ],
    componentRestrictions: { country: 'au' },
};

const AddressAutocomplete = ({
    onChange,
    height,
    options = defaultOptions,
    initialInputValue,
    clearOnSelect = false,
    ...props
}: AddressAutocompleteProps) => {
    const handleSelect = (
        selectedPlace: google.maps.places.PlaceResult | null,
        selectedLabel?: string,
    ) => {
        if (!selectedPlace) return;

        const getAddressComponent = (
            types: string[],
            short_name: boolean = false,
        ) => {
            const component = selectedPlace.address_components?.find((comp) =>
                types.some((type) => comp.types.includes(type)),
            );
            return component
                ? short_name
                    ? component.short_name
                    : component.long_name
                : '';
        };

        const unitNo = getAddressComponent(['subpremise']);

        const address: Address = {
            placeId: selectedPlace.place_id,
            name: selectedPlace.name || selectedPlace.formatted_address,
            displayLabel: selectedLabel || selectedPlace.name,
            streetAddress: `${unitNo ? `${unitNo}/` : ''}${getAddressComponent([
                'street_number',
            ])} ${getAddressComponent(['route'])}`,
            suburb: getAddressComponent(['locality', 'sublocality']),
            state: getAddressComponent(['administrative_area_level_1'], true),
            postcode: getAddressComponent(['postal_code']),
            country: getAddressComponent(['country'], true),
            lat: selectedPlace.geometry?.location?.lat(),
            lng: selectedPlace.geometry?.location?.lng(),
        };

        onChange(address);
    };

    return (
        <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}>
            <PlaceAutocomplete
                onPlaceSelect={handleSelect}
                height={height}
                options={options}
                initialInputValue={initialInputValue}
                clearOnSelect={clearOnSelect}
                {...props}
            />
        </APIProvider>
    );
};

export default AddressAutocomplete;
